import logger from '@goldcast/logger';
import { themeHelper } from '@goldcast/uicomponents-branding';
import studioLookAndFeelSections from '@/studio/lookAndFeel/sections';
import { getCssVarValue, isCssVar, removeSpaces, hexToRgb, isGradientString, isColorString } from '@/utils/utils';
import isequal from 'lodash.isequal';

export default {
  theme_obj(state, getters) {
    return { ...state.brandingSettings, ...getters.getChangesMade };
  },
  getInitialModuleData: (state, getters) => moduleKeys => {
    const latestChanges = {
      ...state.brandingSettings,
      ...getters.getChangesMade
    };

    const moduleData = {};

    moduleKeys.forEach(key => {
      moduleData[key] = latestChanges[key];
    });

    return moduleData;
  },
  themeStyles(state, getters) {
    return themeHelper.themeObjToStyleVariables(getters.theme_obj || {});
  },
  getChangesMade(state) {
    return {
      ...state.changesMadeInEvent,
      ...state.changesMadeInThemeObj
    };
  },
  areThereChanges(state, getters) {
    const changesMade = getters.getChangesMade;
    const { brandingSettings } = state;

    return Object.keys(changesMade).some(key => {
      if (key === 'onEditScreenSaver') return false;

      let changesMadeValue = changesMade[key];
      let brandingSettingsValue = brandingSettings[key];

      // case to handle when one is null/undefined and other is empty string ""
      if (!changesMadeValue && !brandingSettingsValue) return false;

      // case to handle when key changed is a color or a css variable
      if (typeof changesMadeValue === 'string') {
        // convert css variable to its corresponding value from the theme_obj
        if (isCssVar(changesMadeValue)) changesMadeValue = getCssVarValue(changesMadeValue);

        // convert color type from hex to rgba
        if (changesMadeValue?.startsWith('#')) changesMadeValue = hexToRgb(changesMadeValue);

        // remove all the spaces from the color string
        if (isColorString(changesMadeValue) || isGradientString(changesMadeValue))
          changesMadeValue = removeSpaces(changesMadeValue);
      }

      // case to handle when key changed is a color or a css variable
      if (typeof brandingSettingsValue === 'string') {
        // convert css variable to its corresponding value from the theme_obj
        if (isCssVar(brandingSettingsValue)) brandingSettingsValue = getCssVarValue(brandingSettingsValue);

        // convert color type from hex to rgba
        if (brandingSettingsValue?.startsWith('#')) brandingSettingsValue = hexToRgb(brandingSettingsValue);

        // remove all the spaces from the color string
        if (isColorString(brandingSettingsValue) || isGradientString(brandingSettingsValue))
          brandingSettingsValue = removeSpaces(brandingSettingsValue);
      }

      // case to handle when key changed is a javascript object
      if (typeof changesMadeValue === 'object' && typeof brandingSettingsValue === 'object') {
        return !isequal(changesMadeValue, brandingSettingsValue);
      }

      return changesMadeValue !== brandingSettingsValue;
    });
  },
  currentTargets(state, getters, rootState, rootGetters) {
    const section = studioLookAndFeelSections.find(({ id }) => id === state.currentSectionId);
    const currentModule = section?.modules.find(({ id }) => id === state.currentModuleId) || {};

    let targets = currentModule?.preview?.targets ?? [];
    if (state.currentView === 'cellphone' && state.currentModuleId === 'font') {
      targets = currentModule.preview.targets.filter(it => !it.id.includes('agenda-item-specs'));
    }

    return targets;
  },
  currentModule(state, getters, rootState, rootGetters) {
    const section = studioLookAndFeelSections.find(({ id }) => id === state.currentSectionId);
    if (!section) {
      logger.warn('Invalid sectionid!');
      return {};
    } else {
      return section.modules.find(({ id }) => id === state.currentModuleId) || {};
    }
  },
  shouldShowSuccessNotification(state) {
    const changesMadeInEvent = state.changesMadeInEvent;
    const changesMadeInThemeObj = state.changesMadeInThemeObj;
    const brandingSettings = state.brandingSettings;
    const isUpdateBrandingLoading = state.updateBrandingLoading;

    const isChangesMadeInEventEmpty = isEmptyObj(changesMadeInEvent);
    const isChangesMadeInThemeObjEmpty = isEmptyObj(changesMadeInThemeObj);
    const isBrandingSettingsEmpty = isEmptyObj(brandingSettings);

    return (
      isChangesMadeInEventEmpty && isChangesMadeInThemeObjEmpty && !isBrandingSettingsEmpty && !isUpdateBrandingLoading
    );
  },
  navigatorLinks(state, getters) {
    return [
      {
        label: 'Stage',
        name: 'stage',
        count: 0,
        show: true,
        loading: false
      },
      {
        label: 'Agenda',
        name: 'agenda',
        show: true,
        active: false,
        description: ''
      },
      {
        label: getters.theme_obj.discussion_room_tab_title || 'Rooms',
        name: 'discussiongroups',
        show: getters.theme_obj.show_discussion_rooms,
        description: 'Join a round table to discuss moderated topics with your fellow attendees'
      },
      {
        label: getters.theme_obj.booth_tab_title || 'Booths',
        name: 'eventbooths',
        show: getters.theme_obj.show_event_booths,
        description: 'Join a booth to know more about a sponsor'
      },
      {
        label: getters.theme_obj.booth_tab_title_2 || 'Secondary Booths',
        name: 'eventbooths2',
        show: getters.theme_obj.show_event_booths && !!getters.theme_obj.booth_tab_title_2,
        description: 'You are in the booth'
      },
      {
        label: getters.theme_obj.speaker_tab_title || 'Speakers',
        name: 'speaker',
        show: getters.theme_obj.speaker_tab_visible,
        loading: false,
        active: false
      },
      {
        label: getters.theme_obj.people_tab_title || 'People',
        name: 'network',
        show: getters.theme_obj.show_attendees,
        description: 'Connect with your fellow attendees'
      }
    ];
  },
  stageChatTabs(state, getters) {
    let tabs = [
      {
        id: 'speakers',
        label: 'Speakers'
      }
    ];

    if (!getters.theme_obj.broadcast_chat_disabled) {
      tabs.push({
        id: 'session',
        label: 'Session'
      });
    }

    tabs.push({
      id: 'public',
      label: 'Public',
      selected: true,
      icon: getters.theme_obj.public_chat_disabled ? 'lock' : undefined
    });

    return tabs;
  },
  sidebarPrimaryTabs(state, getters) {
    let tabs = [
      {
        name: 'Chat',
        id: 'stage_chat'
      }
    ];

    if (!getters.theme_obj.p2p_chat_disabled) {
      tabs.push({
        name: 'Messages',
        icon: '',
        id: 'messages',
        showIndicator: true,
        available: false,
        indicatorDetails: [
          {
            user: {
              name: 'John Doe',
              image: ''
            },
            count: 2
          }
        ]
      });
    }

    tabs.push({
      name: 'Docs',
      icon: '',
      id: 'docs'
    });

    tabs.push({
      name: 'Polls',
      icon: '',
      id: 'poll'
    });

    tabs.push({
      name: 'Q&A',
      icon: '',
      id: 'qa'
    });

    tabs.push({
      name: 'Media',
      icon: '',
      id: 'media'
    });

    return tabs;
  }
};

function isEmptyObj(obj) {
  return Object.entries(obj).length === 0 && obj.constructor === Object;
}
